import { ErrorHandler, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { captureClientError } from './error-capture';

/**
 * Global Angular error handler that captures uncaught client-side runtime errors
 * (rendering errors, thrown exceptions, unhandled promise rejections routed through
 * Angular) to the backend Error Logs feed under the FRONTEND category.
 *
 * HttpErrorResponse instances are intentionally skipped here — those are already
 * captured (with richer context) by the AuthInterceptor, so handling them again
 * would double-log.
 *
 * This file also owns stale-deployment (lazy chunk) resilience, in two layers:
 *   1. REACTIVE  — `handleChunkLoadError()` turns a failed lazy-route chunk into a
 *                  bounded document load so the user still lands on the page they
 *                  asked for instead of a blank screen or a dead click.
 *   2. PROACTIVE — `checkForNewDeployment()` / `applyPendingDeploymentOnNavigation()`
 *                  notice that the server is serving a newer build and pick it up on
 *                  the next route change, before any chunk can 404.
 * Both layers are environment-agnostic: they key off the build's own entry-script
 * filename, so they behave identically under `ng serve`, the dev container and prod,
 * and they stay completely inert when there is nothing to compare.
 */

// ── URL safety ────────────────────────────────────────────────────────────────

/**
 * Normalizes a navigation target into a safe, same-origin, root-relative path.
 *
 * Both resilience layers turn a router-reported URL into a real document load, so
 * both must reject:
 *   - absolute / protocol-relative URLs (`https://evil.test`, `//evil.test`) — a
 *     document load to an attacker-supplied origin is an open redirect;
 *   - paths still carrying Angular route-matcher tokens (`**` from the wildcard
 *     route, `:id` placeholders) — those are route *patterns*, not addresses, and
 *     navigating to them lands the user on a nonsense URL.
 *
 * @returns `pathname + search + hash` to load, or null when the input cannot be
 *          trusted as a real in-app destination.
 */
function toSafeInternalUrl(raw: string | null | undefined): string | null {
    if (!raw || typeof raw !== 'string' || typeof window === 'undefined') {
        return null;
    }

    const candidate = raw.trim();
    if (!candidate.startsWith('/') || candidate.startsWith('//')) return null;
    if (/\s/.test(candidate)) return null;

    try {
        const resolved = new URL(candidate, window.location.origin);
        if (resolved.origin !== window.location.origin) return null;
        // Route tokens only ever appear in the path, so query values keep their colons.
        if (resolved.pathname.includes('*') || resolved.pathname.includes(':')) return null;
        return `${resolved.pathname}${resolved.search}${resolved.hash}`;
    } catch {
        return null;
    }
}

/**
 * True when the URL fragment carries an MSAL redirect response (`#code=…`,
 * `#error=…`). Replacing the document with a clean path during that handshake
 * discards the response and silently bounces the user back to login, so both
 * layers reload in place instead of navigating while this is true.
 */
function hasAuthResponseFragment(): boolean {
    if (typeof window === 'undefined') return false;
    return /(^|[#&])(code|id_token|access_token|error|state)=/.test(window.location.hash);
}

// ── Stale-deployment (lazy chunk) recovery ────────────────────────────────────
//
// Every build emits content-hashed chunk filenames and each deploy replaces the
// container, deleting the previous build's chunks. A tab that was already open
// (or a browser/proxy holding a stale index.html) still asks for the previous
// deploy's chunk names, so the dynamic import behind a lazy route 404s and the
// user gets a blank page or a dead link.
//
// The only client-side cure is a full document load, which re-fetches index.html
// (served `no-store` by nginx) and therefore the current chunk names. The state
// below bounds that: at most MAX_RECOVERY_ATTEMPTS document loads per incident,
// after which we stop reloading and show a visible notice instead of looping.

const RECOVERY_STATE_KEY = 'wyber_chunk_recovery';
/** Failures further apart than this are treated as a new, unrelated incident. */
const RECOVERY_WINDOW_MS = 30_000;
/** Hard cap on automatic document loads per incident — prevents reload loops. */
const MAX_RECOVERY_ATTEMPTS = 2;
const NOTICE_ELEMENT_ID = 'wyber-chunk-recovery-notice';

interface ChunkRecoveryState {
    /** Timestamp of the most recent recovery attempt. */
    ts: number;
    /** Attempts made in the current incident window. */
    attempts: number;
}

/**
 * True once this document has started a recovery load. One failed chunk surfaces
 * through several channels (navigation-error handler, the rejected navigation
 * promise, Angular's ErrorHandler), and each would otherwise burn an attempt and
 * re-trigger the same navigation. Cleared implicitly: the document is replaced.
 */
let recoveryInFlight = false;

/**
 * Phrases browsers use for a failed `import()` of a missing/broken chunk:
 *   Chrome/Edge — "Failed to fetch dynamically imported module: <url>"
 *   Firefox     — "error loading dynamically imported module"
 *   Safari      — "Importing a module script failed."
 *   webpack     — "Loading chunk N failed" / ChunkLoadError
 *
 * Deliberately specific: matching loose fragments such as `chunk-` + `failed`
 * would also match ordinary API/runtime errors (every prod stack frame contains
 * a `chunk-*.js` filename), and a false positive here reloads the whole app.
 */
const CHUNK_ERROR_PATTERNS = [
    'failed to fetch dynamically imported module',
    'error loading dynamically imported module',
    'failed to load dynamically imported module',
    'importing a module script failed',
    'dynamically imported module',
    'loading chunk',
    'chunk load failed',
    'failed to load chunk',
    'loading css chunk',
    'chunkloaderror',
    'unable to preload css',
    'unexpected token \'<\'',
    'unexpected token <',
    'is not a valid javascript mime type',
    'disallowed mime type',
    'expected a javascript module script',
    'mime type (\'text/html\') is not executable',
    'mime type ("text/html") is not executable',
];

/**
 * Detects if an error is due to a stale dynamic import / lazy chunk 404
 * (e.g. after a new deployment replaced the hashed chunk filenames).
 */
export function isChunkLoadError(error: any): boolean {
    if (!error) return false;

    try {
        // Unwrap the shapes this error can arrive in: raw Error, Angular
        // NavigationError, ErrorEvent, PromiseRejectionEvent reason, wrapped cause.
        const candidates = [
            error,
            error?.error,
            error?.reason,
            error?.cause,
            error?.originalError,
            error?.error?.cause,
            error?.rejection,
            error?.zoneAwareError,
        ];

        for (const candidate of candidates) {
            if (!candidate) continue;
            if (candidate?.name === 'ChunkLoadError' || candidate?.constructor?.name === 'ChunkLoadError') return true;

            const message = String(typeof candidate === 'string' ? candidate : (candidate?.message || '')).toLowerCase();
            const stack = String(candidate?.stack || '').toLowerCase();
            const text = `${message} ${stack}`;
            if (!text.trim()) continue;

            if (CHUNK_ERROR_PATTERNS.some(pattern => text.includes(pattern))) {
                return true;
            }

            // Also match if error is about a missing chunk (404 / failed resource with chunk- or .js)
            if ((text.includes('chunk-') || text.includes('.js')) && (text.includes('404') || text.includes('failed to load resource'))) {
                return true;
            }
        }
    } catch {
        /* detection must never throw */
    }

    return false;
}

function safeSessionGet(key: string): string | null {
    try { return sessionStorage.getItem(key); } catch { return null; }
}

function safeSessionSet(key: string, value: string): void {
    // Storage may be unavailable (private mode / quota) — recovery still proceeds.
    try { sessionStorage.setItem(key, value); } catch { /* ignore */ }
}

function safeSessionRemove(key: string): void {
    try { sessionStorage.removeItem(key); } catch { /* ignore */ }
}

function readRecoveryState(): ChunkRecoveryState {
    try {
        const parsed = JSON.parse(safeSessionGet(RECOVERY_STATE_KEY) || '{}');
        return { ts: Number(parsed?.ts) || 0, attempts: Number(parsed?.attempts) || 0 };
    } catch {
        return { ts: 0, attempts: 0 };
    }
}

function writeRecoveryState(state: ChunkRecoveryState): void {
    safeSessionSet(RECOVERY_STATE_KEY, JSON.stringify(state));
}

/**
 * Clears the recovery counter. Called once a navigation succeeds, so a healthy
 * session never carries an exhausted counter into a later, unrelated incident.
 */
export function resetChunkRecoveryState(): void {
    safeSessionRemove(RECOVERY_STATE_KEY);
    removeChunkRecoveryNotice();
}

/**
 * Cleans up any existing recovery notice element from the DOM.
 */
function removeChunkRecoveryNotice(): void {
    try {
        if (typeof document !== 'undefined') {
            const overlay = document.getElementById(NOTICE_ELEMENT_ID);
            if (overlay) {
                overlay.remove();
            }
        }
    } catch {
        /* never throw during notice removal */
    }
}

// Ensure any lingering recovery overlay from a previous compilation/attempt is immediately cleaned up
if (typeof document !== 'undefined') {
    removeChunkRecoveryNotice();
}

/**
 * Recovers from a stale lazy-chunk load failure with a full document load, which
 * re-fetches index.html and therefore the current chunk manifest.
 *
 * `targetUrl` is the URL the user was actually navigating to (Angular reports it
 * on `NavigationError`). With Angular's default deferred URL update the address
 * bar still shows the *previous* page when a lazy route fails, so a plain reload
 * would silently drop the user back where they started; loading the target URL
 * instead lands them where they asked to go.
 *
 * @returns true only when a reload/navigation was actually started. Callers must
 *          respect a false return and let the error continue through their normal
 *          logging/propagation path — swallowing it would hang the navigation.
 */
export function handleChunkLoadError(error: any, targetUrl?: string | null): boolean {
    if (typeof window === 'undefined' || !isChunkLoadError(error)) {
        return false;
    }

    // The document is already being replaced — don't burn another attempt or fire a
    // second navigation for the same failure arriving through another channel.
    if (recoveryInFlight) {
        return false;
    }

    // A dropped connection produces the same "failed to fetch module" error as a
    // deleted chunk, but reloading while offline replaces a working app with the
    // browser's error page. Offline is therefore reported, never reloaded.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        // eslint-disable-next-line no-console
        console.warn('[ChunkRecovery] Module load failed while offline — not reloading.', error);
        removeChunkRecoveryNotice();
        return false;
    }

    const now = Date.now();
    const state = readRecoveryState();
    // Outside the incident window this is a fresh failure — start counting again.
    const attempts = now - state.ts > RECOVERY_WINDOW_MS ? 0 : state.attempts;

    if (attempts >= MAX_RECOVERY_ATTEMPTS) {
        // eslint-disable-next-line no-console
        console.error(
            `[ChunkRecovery] Chunk still unavailable after ${attempts} reload attempt(s) — `
            + 'aborting recovery to prevent reload loops.', error);
        removeChunkRecoveryNotice();
        return false;
    }

    recoveryInFlight = true;
    writeRecoveryState({ ts: now, attempts: attempts + 1 });

    const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    // Mid-login the fragment carries the MSAL redirect response, so reload in place
    // (which keeps the full URL, fragment included) instead of navigating to a path.
    const destination = hasAuthResponseFragment() ? null : toSafeInternalUrl(targetUrl);
    const target = destination || current;

    // Build URL with cache-busting timestamp query parameter so the browser and intermediate proxies
    // treat the request as an unvisited document and bypass local HTTP/disk caches completely.
    let refreshUrl = target;
    try {
        const safeUrl = new URL(target, window.location.origin);
        safeUrl.searchParams.set('_deploy_refresh', Date.now().toString());
        refreshUrl = `${safeUrl.pathname}${safeUrl.search}${safeUrl.hash}`;
    } catch {
        refreshUrl = target.includes('?') ? `${target}&_deploy_refresh=${Date.now()}` : `${target}?_deploy_refresh=${Date.now()}`;
    }

    // eslint-disable-next-line no-console
    console.warn(
        `[ChunkRecovery] Stale deployment chunk detected (attempt ${attempts + 1}/${MAX_RECOVERY_ATTEMPTS}). `
        + `Navigating fresh to ${refreshUrl} to bust cache...`, error);

    try {
        window.location.replace(refreshUrl);
    } catch {
        window.location.assign(refreshUrl);
    }

    return true;
}

// ── Deployment-version awareness (proactive layer) ────────────────────────────
//
// The recovery above runs *after* a chunk 404. This part prevents most of them:
// it fingerprints the build the current document was served from, notices when
// the server is serving a different build, and upgrades the NEXT route change
// into a full document load — a navigation the user was making anyway, so the
// new build arrives without an error ever being shown.
//
// The fingerprint is the set of build-output filenames index.html references —
// its entry scripts plus its `modulepreload` chunks — read the same way from the
// live document and from a freshly fetched index.html:
//   - container builds (`outputHashing: "none"`) → main.js never changes, but the
//     preloaded chunk-<HASH>.js names change on every build, so deploys are still
//     detected;
//   - hashed builds (`outputHashing: "all"`)     → main-<HASH>.js changes too;
//   - `ng serve` (no hashing, no preloads)       → identical on both sides, so it
//     never reports a false "new build".
// If anything is unknown (no references found, fetch blocked, offline) it reports
// "no new deployment" and stays inert.

/** Marker recording the build a proactive full load was expected to deliver. */
const EXPECTED_BUILD_KEY = 'wyber_deploy_expected_build';
/** Minimum spacing between index.html checks. */
const DEPLOY_CHECK_THROTTLE_MS = 60_000;
/**
 * Build-output filenames, hashed or not: main.js, main-<HASH>.js, polyfills.js,
 * styles.css, chunk-<HASH>.js. Anything else (CDN scripts, hand-named assets,
 * Vite's dev client) is ignored so it cannot skew the comparison.
 */
const BUILD_OUTPUT_PATTERN = /^(?:main|polyfills|styles|scripts|chunk)(?:[.-][A-Za-z0-9]+)?\.(?:js|css)$/i;

/** Fingerprint of the build this document was served from. */
let documentBuild: string | null = null;
/** Fingerprint the server is currently serving, once known to differ. */
let pendingBuild: string | null = null;
let lastDeployCheckTs = 0;
let deployCheckInFlight: Promise<boolean> | null = null;
/**
 * Set when a proactive full load failed to deliver the build it promised (a
 * caching layer we cannot influence is pinning this client to an old index.html).
 * Latching this off guarantees we can never full-load on a loop.
 */
let proactiveReloadDisabled = false;
/** Proactive loads stay off until the app has completed one navigation. */
let appReady = false;

/**
 * Reduces a list of URLs to a stable fingerprint of the build they belong to:
 * build-output basenames only, de-duplicated and sorted so neither document
 * order nor how the list was gathered can affect the comparison.
 */
function toBuildFingerprint(urls: readonly string[]): string | null {
    const names = new Set<string>();
    for (const url of urls) {
        const name = (url.split('?')[0].split('#')[0].split('/').pop() || '');
        if (BUILD_OUTPUT_PATTERN.test(name)) {
            names.add(name);
        }
    }
    if (!names.size) return null;
    return Array.from(names).sort().join('|');
}

/** Fingerprints the build that served the currently executing document. */
function readDocumentBuild(): string | null {
    try {
        const urls = Array.from(
            document.querySelectorAll('script[src], link[rel="modulepreload"][href], link[rel="stylesheet"][href]')
        ).map(el => el.getAttribute('src') || el.getAttribute('href') || '');
        return toBuildFingerprint(urls);
    } catch {
        return null;
    }
}

/** Fingerprints the build described by a freshly fetched index.html. */
function readBuildFromHtml(html: string): string | null {
    try {
        const urls: string[] = [];
        const refPattern = /\b(?:src|href)\s*=\s*["']([^"']+)["']/gi;
        let match: RegExpExecArray | null;
        while ((match = refPattern.exec(html)) !== null) {
            urls.push(match[1]);
        }
        return toBuildFingerprint(urls);
    } catch {
        return null;
    }
}

/**
 * Records the build fingerprint of the document currently executing.
 * Call once at bootstrap, before any runtime-injected script can appear.
 */
export function captureCurrentBuildFingerprint(): void {
    if (typeof document === 'undefined') {
        return;
    }

    documentBuild = readDocumentBuild();

    // Verify the outcome of a previous proactive load: if the document we just
    // booted is still NOT the build that load promised, some cache outside our
    // control is pinning this client. Stop doing proactive loads rather than
    // repeating one on every navigation.
    const expected = safeSessionGet(EXPECTED_BUILD_KEY);
    if (expected) {
        safeSessionRemove(EXPECTED_BUILD_KEY);
        if (documentBuild && documentBuild !== expected) {
            proactiveReloadDisabled = true;
            // eslint-disable-next-line no-console
            console.warn(
                `[DeploymentVersion] Expected build "${expected}" after refresh but got `
                + `"${documentBuild}" — disabling proactive reloads for this session.`);
        }
    }
}

/** Marks the app as having completed a navigation; enables proactive loads. */
export function markAppReady(): void {
    appReady = true;
}

/**
 * Fetches index.html and compares its entry script to this document's.
 * Throttled, single-flight, and silent on every failure.
 *
 * @returns true when the server is serving a different build.
 */
export function checkForNewDeployment(force = false): Promise<boolean> {
    if (typeof window === 'undefined' || typeof fetch !== 'function') {
        return Promise.resolve(false);
    }
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        return Promise.resolve(false);
    }
    if (pendingBuild) {
        return Promise.resolve(true);
    }
    // Nothing to compare against — never guess.
    if (!documentBuild) {
        return Promise.resolve(false);
    }
    if (deployCheckInFlight) {
        return deployCheckInFlight;
    }

    const now = Date.now();
    if (!force && now - lastDeployCheckTs < DEPLOY_CHECK_THROTTLE_MS) {
        return Promise.resolve(false);
    }
    lastDeployCheckTs = now;

    // Absolute path on purpose: a relative 'index.html' would resolve against the
    // current route (`/rfp/123/index.html`) instead of the app's document.
    // Query parameter ensures intermediate proxies never return a stale cached index.html.
    deployCheckInFlight = fetch(`/index.html?_deploy_check=${now}`, { cache: 'no-store', credentials: 'same-origin' })
        .then(response => (response.ok ? response.text() : null))
        .then(html => {
            if (!html) return false;
            const serverBuild = readBuildFromHtml(html);
            if (!serverBuild || serverBuild === documentBuild) {
                return false;
            }
            pendingBuild = serverBuild;
            // eslint-disable-next-line no-console
            console.warn(
                `[DeploymentVersion] New build detected (document "${documentBuild}" → `
                + `server "${serverBuild}"). It will be applied on the next route change.`);
            return true;
        })
        .catch(() => false)
        .then(result => {
            deployCheckInFlight = null;
            return result;
        });

    return deployCheckInFlight;
}

/**
 * Starts a light background watch: re-checks when the tab becomes visible, regains
 * focus, or comes back online (throttled). No timers, so an idle tab costs nothing.
 */
export function startDeploymentWatch(): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return;
    }

    const check = () => { void checkForNewDeployment(); };

    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') check();
    });
    window.addEventListener('focus', check);
    window.addEventListener('online', check);
}

/**
 * Called on every route change. When a newer build is already known to be live,
 * the route change is upgraded into a full document load of the SAME target URL,
 * so the user lands exactly where they were going — on the new build, with no
 * chunk error in between. Otherwise it quietly primes the next check.
 *
 * @returns true when a full document load was started (the SPA navigation that
 *          triggered this is then irrelevant — the document is being replaced).
 */
export function applyPendingDeploymentOnNavigation(targetUrl: string | null | undefined): boolean {
    if (typeof window === 'undefined') {
        return false;
    }
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        return false;
    }

    if (!pendingBuild) {
        // Keep the knowledge fresh for the next navigation; never blocks this one.
        void checkForNewDeployment();
        return false;
    }

    if (proactiveReloadDisabled || !appReady) {
        return false;
    }

    // Mid-login the URL fragment carries the MSAL redirect response; replacing the
    // document with a clean path would discard it and bounce the user to login.
    if (hasAuthResponseFragment()) {
        return false;
    }

    const destination = toSafeInternalUrl(targetUrl);
    if (!destination) {
        return false;
    }

    // Auth-critical routes run redirect handshakes that must not be restarted.
    if (/^\/(auth|logout|login|welcome)\b/.test(destination)) {
        return false;
    }

    safeSessionSet(EXPECTED_BUILD_KEY, pendingBuild);
    // eslint-disable-next-line no-console
    console.warn(`[DeploymentVersion] Applying new build "${pendingBuild}" via fresh load of ${destination}`);
    pendingBuild = null;

    let refreshUrl = destination;
    try {
        const safeUrl = new URL(destination, window.location.origin);
        safeUrl.searchParams.set('_deploy_refresh', Date.now().toString());
        refreshUrl = `${safeUrl.pathname}${safeUrl.search}${safeUrl.hash}`;
    } catch {
        refreshUrl = destination.includes('?') ? `${destination}&_deploy_refresh=${Date.now()}` : `${destination}?_deploy_refresh=${Date.now()}`;
    }

    try {
        window.location.replace(refreshUrl);
    } catch {
        window.location.assign(refreshUrl);
    }
    return true;
}

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
    handleError(error: any): void {
        try {
            // Auto-recover from stale lazy-loaded chunk 404s following deployments.
            // A false return means recovery is exhausted/unavailable, so the error
            // falls through and is still logged and reported below.
            if (handleChunkLoadError(error)) {
                return;
            }

            // HTTP errors are captured in the interceptor; don't double-log.
            const isHttp = error instanceof HttpErrorResponse
                || error?.name === 'HttpErrorResponse'
                || typeof error?.status === 'number' && error?.url !== undefined;

            if (!isHttp) {
                const message = error?.message || String(error);
                const stack = error?.stack || '';
                captureClientError({
                    category: 'FRONTEND',
                    error: message,
                    errorDetail: `${stack}\nURL: ${typeof location !== 'undefined' ? location.href : ''}`,
                    source: typeof location !== 'undefined' ? location.pathname : 'app',
                });
            }
        } catch {
            /* never let error capture throw */
        }

        // Preserve default behavior so devs still see the error in the console.
        // eslint-disable-next-line no-console
        console.error(error);
    }
}

/**
 * Registers window-level listeners for errors Angular's ErrorHandler doesn't see:
 * resource/page-load errors and unhandled promise rejections outside the zone.
 * Call once during app bootstrap.
 */
export function registerWindowErrorCapture(): void {
    if (typeof window === 'undefined') {
        return;
    }

    window.addEventListener('error', (event: ErrorEvent) => {
        if (handleChunkLoadError(event?.error || event?.message)) {
            return;
        }

        // Ignore benign resource-load noise without a message.
        const message = event?.message || (event?.error && event.error.message);
        if (!message) {
            return;
        }
        captureClientError({
            category: 'FRONTEND',
            error: message,
            errorDetail: `${event.error?.stack || ''}\nAt: ${event.filename}:${event.lineno}:${event.colno}\nURL: ${location.href}`,
            source: location.pathname,
        });
    });

    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
        const reason: any = event?.reason;
        if (handleChunkLoadError(reason)) {
            return;
        }

        // Skip HTTP rejections — captured by the interceptor.
        if (reason && (reason.name === 'HttpErrorResponse' || typeof reason.status === 'number')) {
            return;
        }
        const message = reason?.message || String(reason);
        captureClientError({
            category: 'FRONTEND',
            error: `Unhandled promise rejection: ${message}`,
            errorDetail: `${reason?.stack || ''}\nURL: ${location.href}`,
            source: location.pathname,
        });
    });
}
